// SCR-01 login, first-login gates, session (plan 8.4.1, 11.4). Server session cookie + CSRF:
// cookie `aiax_session` (HttpOnly, SameSite=Lax, Secure) and `aiax_csrf` (readable) → header `x-csrf-token` on every
// state-changing request. Shapes of options/login/mfa/password/setup/logout/reauth/me follow the web worker's contract.
import { z } from 'zod';
import { AuthProvider, ConsentState, RecordLevel, SYSTEM_ROLES } from '../enums.js';
import { PermissionMap } from '../permissions.js';
import { MenuId, RoleKind } from '../roles-menus.js';
import { EpochMs, Id, LocalizedText, OkResponse, RoleId, TeamRef, UserId } from '../common.js';

/** GET /api/v1/auth/options — what the login card can offer. `setupRequired` = no users yet (first admin). */
export const AuthOptionsResponse = z.object({
  google: z.boolean(),
  setupRequired: z.boolean(),
  /** Allowed company domain shown under the Google button (hint only; the server re-checks). */
  googleDomain: z.string().nullable().optional(),
  lockAfterFailures: z.int().positive().optional(),
  lockMinutes: z.int().positive().optional(),
});
export type AuthOptionsResponse = z.infer<typeof AuthOptionsResponse>;

export const LoginRequest = z.object({
  /** Login id or e-mail (case-insensitive). */
  loginId: z.string().trim().min(1).max(254),
  password: z.string().min(1).max(256),
});
export type LoginRequest = z.infer<typeof LoginRequest>;

export const LOGIN_STATUSES = ['ok', 'mfa_required', 'password_change_required', 'locked'] as const;
export const LoginStatus = z.enum(LOGIN_STATUSES);
export type LoginStatus = z.infer<typeof LoginStatus>;
/**
 * POST /api/v1/auth/login (also /mfa). Always HTTP 200 for these four outcomes. Wrong credentials are 401
 * `invalid_credentials` with `details.remainingAttempts` (same response/time for unknown accounts, plan 11.4.1).
 * `locked` carries the unlock instant; `lockMinutes` lets the web draw the countdown dial.
 */
export const LoginResponse = z
  .object({
    status: LoginStatus,
    lockedUntil: EpochMs.optional(),
    retryAfterSec: z.int().nonnegative().optional(),
    lockMinutes: z.int().positive().optional(),
  })
  .superRefine((r, ctx) => {
    if (r.status === 'locked' && r.lockedUntil == null) ctx.addIssue({ code: 'custom', message: 'locked needs lockedUntil', path: ['lockedUntil'] });
  });
export type LoginResponse = z.infer<typeof LoginResponse>;

/** POST /api/v1/auth/mfa — 6-digit TOTP after a password login that answered `mfa_required`. */
export const MfaRequest = z.object({ code: z.string().regex(/^\d{6}$/) });
export type MfaRequest = z.infer<typeof MfaRequest>;

/**
 * POST /api/v1/auth/password — change password (first-login gate and settings). Rules (plan 11.4.1): ≥ 10 chars,
 * 3 of 4 classes, must not contain the login id, not a common password. Rotates the current session, ends others.
 */
export const PasswordChangeRequest = z.object({ current: z.string().min(1).max(256), next: z.string().min(1).max(256) });
export type PasswordChangeRequest = z.infer<typeof PasswordChangeRequest>;
export const PASSWORD_RULES = ['min_length', 'classes', 'contains_login_id', 'common_password', 'same_as_current'] as const;
export const PasswordRule = z.enum(PASSWORD_RULES);
/** 422 `password_policy` details: `{ failed: PasswordRule[] }`. */
export const PasswordPolicyDetails = z.object({ failed: z.array(PasswordRule) });
export const PasswordChangeResponse = z.object({ status: z.literal('ok'), endedOtherSessions: z.int().nonnegative() });
export type PasswordChangeResponse = z.infer<typeof PasswordChangeResponse>;

/** POST /api/v1/auth/setup — first admin with the one-time setup code written to the data folder (plan 11.4.4). */
export const SetupRequest = z.object({
  code: z.string().trim().min(8).max(64),
  loginId: z.string().trim().regex(/^[a-zA-Z0-9._-]{3,64}$/),
  password: z.string().min(1).max(256),
  displayName: z.string().trim().min(1).max(60),
  email: z.email().max(254),
});
export type SetupRequest = z.infer<typeof SetupRequest>;
/** After setup the admin signs in normally (MFA enrolment is then a gate). */
export const SetupResponse = z.object({ status: z.literal('ok') });
export type SetupResponse = z.infer<typeof SetupResponse>;

/** POST /api/v1/auth/reauth — step-up before opening someone else's text (plan 7.5.1 ③, 11.4.3). */
export const ReauthRequest = z.union([z.object({ password: z.string().min(1).max(256) }).strict(), z.object({ code: z.string().regex(/^\d{6}$/) }).strict()]);
export type ReauthRequest = z.infer<typeof ReauthRequest>;
export const ReauthResponse = z.object({ reauthUntil: EpochMs });
export type ReauthResponse = z.infer<typeof ReauthResponse>;

/**
 * Same-site browser path the web app routes (D-W03: `/logs/1330?tab=qa`, no hash, no scheme/host, not `//…`).
 * Google sign-in comes back here (BUILD-SPEC-api API-41).
 */
export const RETURN_PATH_PATTERN = /^\/(?!\/)[\w\-/?=&.,~%]*$/;
export const ReturnPath = z.string().regex(RETURN_PATH_PATTERN).max(500).refine((path) => {
  try {
    const decoded = decodeURIComponent(path.split('?')[0]!);
    return decoded.startsWith('/') && !decoded.startsWith('//') && !/[\\\s\u0000-\u001f\u007f]/.test(decoded);
  } catch {
    return false;
  }
}, 'same-site browser path required');

/** GET /api/v1/auth/google/start — 302 to Google (PKCE S256, state, nonce). reauth uses prompt=login&max_age. */
export const GoogleStartQuery = z.object({
  purpose: z.enum(['login', 'reauth']).default('login'),
  /** Where to go after success: a same-site browser path only (e.g. `/logs/1330?tab=qa`). */
  returnTo: ReturnPath.optional(),
});
/**
 * GET /api/v1/auth/google/callback — failure: 302 to the browser path `/login?gerr=<GoogleError>` (+ `&next=<returnTo>`
 * when one was given); success: 302 to `returnTo` or `/home` (browser paths, not hash routes — API-41).
 */
export const GoogleCallbackQuery = z.object({ code: z.string().max(2048).optional(), state: z.string().max(512).optional(), error: z.string().max(200).optional() });
export const GOOGLE_ERRORS = ['domain', 'cancel', 'unlinked', 'other'] as const;
export const GoogleError = z.enum(GOOGLE_ERRORS);

/** Invite activation (plan 7.1.4 초대 코드, 7 days). GET shows who it is for; POST sets the password. */
export const InviteLookupQuery = z.object({ code: z.string().trim().min(8).max(64) });
export const InviteLookupResponse = z.object({ valid: z.boolean(), emailMasked: z.string().nullable(), team: z.string().nullable(), expiresAt: EpochMs.nullable() });
export const InviteAcceptRequest = z.object({
  code: z.string().trim().min(8).max(64),
  loginId: z.string().trim().regex(/^[a-zA-Z0-9._-]{3,64}$/),
  password: z.string().min(1).max(256),
  displayName: z.string().trim().min(1).max(60),
});

/** Prohibited-use pledge (plan 7.7): versioned text; signing = typing one's own name. */
export const PledgeResponse = z.object({
  version: z.string(),
  hash: z.string(),
  statements: z.array(LocalizedText).min(1),
  ackedVersion: z.string().nullable(),
});
export const PledgeAckRequest = z.object({ version: z.string(), hash: z.string(), signature: z.string().trim().min(1).max(60) });

/** MFA enrolment (P3): show the secret once, then verify a code. */
export const MfaEnrollResponse = z.object({ secret: z.string().regex(/^[A-Z2-7]{16,64}$/), otpauthUri: z.string().startsWith('otpauth://totp/') });
export const MfaVerifyRequest = MfaRequest;

/**
 * First-login gates in the order of plan 11.4.4 (table "첫 로그인 관문": 1 임시 비밀번호 변경 → 2 금지 용도 약정 →
 * 3 다단계 인증 등록 → 4 안내문 동의·기록 수준 고르기; 11.3 places consent before any recording and 8.4.8's flow goes
 * on to the connection wizard after it). `/auth/me` lists the open gates in this order and the web walks them in this
 * order (API-42). While any of the first three is open, only gate/me/logout APIs work (others 403 <gate code>).
 */
export const GATES = ['password_change', 'policy_ack', 'mfa_enroll', 'consent'] as const;
export const Gate = z.enum(GATES);
export type Gate = z.infer<typeof Gate>;
/** Open gates sorted into the 11.4.4 order (unknown values dropped, duplicates removed). */
export const orderGates = (gates: readonly string[]): Gate[] => GATES.filter((g) => gates.includes(g));
export const GATE_ERROR_CODE: Record<Exclude<Gate, 'consent'>, 'password_change_required' | 'policy_ack_required' | 'mfa_enroll_required'> = {
  password_change: 'password_change_required',
  policy_ack: 'policy_ack_required',
  mfa_enroll: 'mfa_enroll_required',
};

export const HeldRole = z.object({
  roleId: RoleId,
  kind: RoleKind,
  /** Custom role names; system roles are named by the web. */
  name: LocalizedText.nullable(),
  /** Applied teams of this grant ('team' scope); empty = company-wide or self. */
  teamIds: z.array(Id),
  expiresAt: EpochMs.nullable(),
  expiryDeferred: z.boolean().default(false),
});
export type HeldRole = z.infer<typeof HeldRole>;

export const UI_LANGS = ['ko', 'en'] as const;
export const THEMES = ['light', 'dark', 'system'] as const;
export const NUMBER_UNITS = ['compact', 'full'] as const;
export const FONT_SIZES = ['m', 'l', 'xl'] as const;
/** IANA time zone name (`Asia/Seoul`, `America/New_York`, `UTC`); the server re-checks it with Intl. */
export const TimeZoneName = z.string().max(64).refine((name) => {
  try { new Intl.DateTimeFormat('en', { timeZone: name }).format(0); return true; } catch { return false; }
}, 'valid IANA time zone required');
/**
 * Time zones offered in the settings select (plan 6.9 ① "화면은 사용자가 고른 시간대", NFR-24); other IANA names are
 * accepted too. Day/week/month boundaries of aggregates stay in the organization time zone (6.9 ⑦).
 */
export const TIME_ZONE_CHOICES = ['Asia/Seoul', 'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Kolkata', 'Europe/London', 'Europe/Berlin', 'America/New_York', 'America/Los_Angeles', 'UTC'] as const;
export const DisplayPrefs = z.object({
  lang: z.enum(UI_LANGS),
  theme: z.enum(THEMES),
  numberUnit: z.enum(NUMBER_UNITS),
  fontSize: z.enum(FONT_SIZES),
  wrapLongText: z.boolean(),
  /** Display time zone for times on screen; null = the organization time zone (`/auth/me` org.timezone). */
  timezone: TimeZoneName.nullable(),
});
export type DisplayPrefs = z.infer<typeof DisplayPrefs>;

/**
 * GET /api/v1/auth/me — who am I and what can I open. Effective permissions are the union of all held roles
 * (member always included, plan 7.1.3); menus/bottom tabs are computed with `menuFor`/`bottomTabsFor`.
 */
export const MeResponse = z.object({
  user: z.object({
    id: UserId,
    loginId: z.string().nullable(),
    displayName: z.string(),
    email: z.string().nullable(),
    /** U-000001 style code; also used in the content-view watermark. */
    userCode: z.string(),
    pseudonym: z.string().regex(/^[0-9A-F]{4}$/),
    avatarHue: z.int().min(0).max(359),
    title: z.string().nullable(),
    authProvider: AuthProvider,
  }),
  roles: z.array(HeldRole),
  /** Role badge in the header: the most privileged held role (admin > delegated_admin > manager > content_reviewer > log_reviewer > ops > stats_viewer > custom > member). */
  primaryRole: RoleId,
  permissions: PermissionMap,
  /** Teams the 'team' scope resolves to (applied/managed teams; not automatically one's own team). */
  scopeTeams: z.array(TeamRef),
  /** Current memberships (소속 팀) with parent. */
  teams: z.array(TeamRef),
  menu: z.array(MenuId),
  bottomTabs: z.array(MenuId),
  gates: z.array(Gate),
  session: z.object({
    adminSession: z.boolean(),
    idleExpiresAt: EpochMs,
    absoluteExpiresAt: EpochMs,
    reauthUntil: EpochMs.nullable(),
    mfa: z.enum(['enrolled', 'not_enrolled', 'not_required', 'google']),
  }),
  /** Drives the '기록 중' band and the "records stopped because…" alerts. */
  consent: z.object({ state: ConsentState, level: RecordLevel.nullable(), pausedUntil: EpochMs.nullable(), noticeVersion: z.string().nullable() }),
  prefs: DisplayPrefs,
  org: z.object({
    timezone: z.string(),
    smallGroupMin: z.int().min(1),
    allowValueSort: z.boolean(),
    googleEnabled: z.boolean(),
    /** P4 API gateway turned on (stats 'api' item, app connection tab). */
    gatewayEnabled: z.boolean(),
    /** Collection paths shown in help "기록은 어디서 오나요" (C1..C6 on/off). */
    collectionPaths: z.array(z.object({ id: z.enum(['C1', 'C2', 'C3', 'C4', 'C5', 'C6']), on: z.boolean() })),
  }),
  unreadNotifications: z.int().nonnegative(),
  serverNow: EpochMs,
});
export type MeResponse = z.infer<typeof MeResponse>;

export const LogoutResponse = OkResponse;

/** Role id priority for `primaryRole` (custom roles sit between stats_viewer and member). */
export const PRIMARY_ROLE_ORDER = ['admin', 'delegated_admin', 'manager', 'content_reviewer', 'log_reviewer', 'ops', 'stats_viewer'] as const satisfies readonly (typeof SYSTEM_ROLES)[number][];

export const authSchemas = {
  AuthOptionsResponse,
  LoginRequest,
  LoginResponse,
  MfaRequest,
  PasswordChangeRequest,
  PasswordChangeResponse,
  PasswordPolicyDetails,
  SetupRequest,
  SetupResponse,
  ReauthRequest,
  ReauthResponse,
  GoogleStartQuery,
  GoogleCallbackQuery,
  InviteLookupQuery,
  InviteLookupResponse,
  InviteAcceptRequest,
  PledgeResponse,
  PledgeAckRequest,
  MfaEnrollResponse,
  MfaVerifyRequest,
  MeResponse,
  LogoutResponse,
  DisplayPrefs,
} as const;
